import * as React from 'react';
import { YMInitializer } from 'react-yandex-metrika';
import { memo, useEffect, useState } from 'react';

import { DATA_LAYER_KEY } from '../../analytics';
import { getLocale } from '../../utils';
import { useConfig } from '../../context';
import SbesAds from './SbesAds';
import { Maybe } from '@websky/graphql';

enum AnalyticsEnum {
	Yandex,
	SbesAds
}

type AccountsToInitializeType = {
	[AnalyticsEnum.Yandex]: number[];
	[AnalyticsEnum.SbesAds]?: Maybe<number>;
};

const defaultAccountsToInitialize: AccountsToInitializeType = {
	[AnalyticsEnum.Yandex]: [],
	[AnalyticsEnum.SbesAds]: undefined
};

const isProd = process.env.NODE_ENV === 'production';

const AnalyticsComponent: React.FC = memo(() => {
	const {
		global: { useYandexMetrika, useSberAds, yandexMetrikaId, sberAdsId }
	} = useConfig();

	// default websky metrika account
	const metrikaAccounts: number[] = [94079362];

	if (yandexMetrikaId) {
		if (Array.isArray(yandexMetrikaId)) {
			metrikaAccounts.push(...yandexMetrikaId.map(it => parseInt(it, 10)));
		} else {
			metrikaAccounts.push(parseInt(yandexMetrikaId, 10));
		}
	}

	const [accountsToInitialize, setAccountsToInitialize] = useState<AccountsToInitializeType>(
		defaultAccountsToInitialize
	);

	useEffect(() => {
		setTimeout(() => {
			// setTimout because when this component uses in multiple places we need time gap between yandex metrica initializations
			const alreadyInitializedAccounts = window.yandex_metrika_accounts || [];

			setAccountsToInitialize({
				[AnalyticsEnum.Yandex]: metrikaAccounts.filter(
					account => !alreadyInitializedAccounts.includes(account)
				),
				[AnalyticsEnum.SbesAds]: !window.top100?.Ch?.[sberAdsId] ? +sberAdsId : undefined
			});
		}, 0);
	}, []);

	const yandexMetricsIds = accountsToInitialize[AnalyticsEnum.Yandex];
	const sberMetricsId = accountsToInitialize[AnalyticsEnum.SbesAds];

	const showYandexMetrics = useYandexMetrika && yandexMetricsIds.length > 0;
	const showSberMetrics = useSberAds && typeof sberMetricsId === 'number' && sberMetricsId > 0;

	return (
		<>
			{showYandexMetrics && (
				<YMInitializer
					version="2"
					accounts={yandexMetricsIds}
					options={{
						clickmap: true,
						trackLinks: true,
						accurateTrackBounce: true,
						webvisor: true,
						trackHash: true,
						ecommerce: DATA_LAYER_KEY,
						params: {
							locale: getLocale()
						}
					}}
				/>
			)}

			{showSberMetrics && <SbesAds id={sberMetricsId} />}
		</>
	);
});

const Analytics: React.FC = memo(() => {
	const {
		global: { collectMetrics }
	} = useConfig();

	if (!isProd || !collectMetrics) {
		return null;
	}

	return <AnalyticsComponent />;
});

export default Analytics;
